import { clearSelectedService } from "@/stores/service";
import type { AdminUser } from "@/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type { AdminUser };

/** Active impersonation session — token + who we are pretending to be. */
export interface Impersonation {
  token: string;
  admin: { id: string; email: string; name: string };
}

interface AuthState {
  token: string | null;
  user: AdminUser | null;
  impersonation: Impersonation | null;
  login: (token: string) => void;
  setUser: (user: AdminUser | null) => void;
  startImpersonation: (imp: Impersonation) => void;
  stopImpersonation: () => void;
  logout: () => void;
}

/**
 * Admin auth store. Custom email/password → JWT (no external auth SDK).
 * Persisted to localStorage so a refresh keeps the operator signed in;
 * the axios client reads `token` for the Authorization: Bearer header.
 *
 * Impersonation keeps the real token untouched — a separate short-lived
 * token is preferred by `getAuthToken` while set, so exiting impersonation
 * restores the original session without re-login.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      impersonation: null,
      login: (token) => set({ token, impersonation: null }),
      setUser: (user) => set({ user }),
      startImpersonation: (imp) => set({ impersonation: imp }),
      stopImpersonation: () => set({ impersonation: null }),
      logout: () => {
        clearSelectedService();
        set({ token: null, user: null, impersonation: null });
      },
    }),
    {
      name: "meshcore-admin-auth",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        impersonation: state.impersonation,
      }),
    }
  )
);

/** Non-reactive token read for the axios interceptor. */
export function getAuthToken(): string | null {
  const { impersonation, token } = useAuthStore.getState();
  return impersonation?.token ?? token;
}

/**
 * On 401 while impersonating: drop the (expired) impersonation token and
 * reload back into the real superadmin session. Returns true when handled.
 */
export function exitImpersonationIfActive(): boolean {
  const { impersonation, stopImpersonation } = useAuthStore.getState();
  if (!impersonation) return false;
  stopImpersonation();
  window.location.reload();
  return true;
}

/** Clear auth + bounce to the login screen (used by the 401 interceptor). */
export function clearAuthAndRedirect(): void {
  useAuthStore.getState().logout();
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin/login")) {
    window.location.assign("/admin/login");
  }
}
